import classNames from "classnames";
import {
	Anchor,
	AppIcon,
	FontIconName,
	Icon
} from "client/Components";
import {
	generateTrackingId,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { range } from "client_legacy/ndaw/arrayHelpers";
import { RenderImageType } from "client/Api/AppIcon/common";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { FrequentlyUsedTileModel } from "client/Api/Workspace/workspace";
import { ReactElement, useCallback } from "react";
import styles from "./FrequentlyUsedTilesList.module.less";

export type FrequentlyUsedTilesListProps = {
	items: readonly FrequentlyUsedTileModel[] | undefined;
	onInvokeTileNavigation: (item: FrequentlyUsedTileModel) => void;
	trackingId: string;
};

export const FrequentlyUsedTilesList = (props: FrequentlyUsedTilesListProps): ReactElement => {
	const {
		items,
		onInvokeTileNavigation,
		trackingId
	} = props;

	const renderItems = (): ReactElement => {
		if (isNil(items)) {
			return (
				<>
					{range(3)
						.map((x): ReactElement => (
							<Skeleton key={x * -1} />
					))}
				</>
			);
		}

		return (
			<>
				{items.map((x): ReactElement => (
					<Item
						key={x.tile.id}
						item={x}
						onInvoke={onInvokeTileNavigation}
					/>
				))}
			</>
		);
	};

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{renderItems()}
		</ul>
	);
};
FrequentlyUsedTilesList.displayName = "FrequentlyUsedTilesList";

const Skeleton = (): ReactElement => {
	return (
		<li className={styles.item}>
			<div
				className={classNames(
					styles.appIcon,
					styles.appIcon_skeleton
				)}
			/>

			<span
				className={classNames(
					styles.name,
					styles.name_skeleton
				)}
			/>
		</li>
	);
};
Skeleton.displayName = "Skeleton";

type ItemProps = {
	item: FrequentlyUsedTileModel;
	onInvoke: (item: FrequentlyUsedTileModel) => void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		onInvoke
	} = props;

	const {
		tile,
		conditionalAccess,
		maintenanceWindow
	} = item;

	const { colorClass, colorStyle } = getReactColorAttributes(tile.color);

	const clickHandler = useCallback(
		(): void => {
			onInvoke(item);
		},
		[item, onInvoke]
	);

	const statusIcon: FontIconName | undefined = conditionalAccess
		? "ban"
		: maintenanceWindow
			? "screwdriver-wrench"
			: undefined;

	const { renderType } = tile.imageDisplayInfo;

	return (
		<li>
			<Anchor
				trackingId={generateTrackingId("item", `|${tile.id}`)}
				key={tile.id}
				className={classNames(
					styles.item,
					styles.item_clickable
				)}
				onClick={clickHandler}
			>
				<div
					className={classNames(
						styles.appIcon,
						{
							[styles.appIcon_withStatus]: maintenanceWindow || conditionalAccess,
							[styles.appIcon_raster]: renderType === RenderImageType.Raster,
							[styles.appIcon_colorizedSvg]: renderType === RenderImageType.ColorizableSvg
						},
						colorClass
					)}
					style={colorStyle}
				>
					<AppIcon
						value={tile.imageDisplayInfo}
						color="inherit"
					/>
				</div>

				<span
					className={styles.name}
					title={tile.name}
				>
					{tile.name}
				</span>

				{statusIcon && (
					<Icon
						value={{
							kind: "font",
							name: statusIcon,
							color: "danger"
						}}
					/>
				)}
			</Anchor>
		</li>
	);
};
Item.displayName = "Item";
